import {
  type QueryObserverOptions,
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";

import { useApi } from "./useApi";

import AgreementClient from "../clients/AgreementClient";

export const useAgreementDocusealTemplate = (id?: string) => {
  const agreementClient = useApi(AgreementClient);

  return useQuery({
    queryKey: ["sign", "templates", "docuseal", id],
    queryFn: () => agreementClient.docusealTemplate(id),
    enabled: !!id,
  });
};

export const useGetDocusealTemplates = (options: UseQueryOptions = {}) => {
  const agreementClient = useApi(AgreementClient);

  return useQuery({
    queryKey: ["sign", "templates", "docuseal"],
    queryFn: () => agreementClient.docusealTemplates(),
    ...options,
  });
};

export const useCreateSubmission = (options: UseMutationOptions = {}) => {
  const agreementApi = useApi(AgreementClient);

  return useMutation(
    ({
      investmentId,
      manualSetup,
    }: {
      investmentId: string;
      manualSetup: boolean;
    }) => agreementApi.createSubmission(investmentId, manualSetup),
    options,
  );
};

export const useCancelInvestmentAgreement = () => {
  const agreementApi = useApi(AgreementClient);

  return useMutation(
    ({
      investmentId,
      investmentAgreementId,
      reason,
      notify_investor,
    }: {
      investmentId: string;
      investmentAgreementId: string;
      reason: string;
      notify_investor: boolean;
    }) => agreementApi.cancelAgreement(investmentId, investmentAgreementId, reason, notify_investor),
  );
};

export const useAgreementSigner = (
  investmentAgreementId: string,
  signatureId: string,
  options: QueryObserverOptions = {},
) => {
  const agreementApi = useApi(AgreementClient);

  return useQuery({
    queryKey: ["investment", "signers", investmentAgreementId, signatureId],
    queryFn: () => agreementApi.additionalSignerDetails(investmentAgreementId, signatureId),
    enabled: !!(investmentAgreementId && signatureId),
    ...options,
  });
};

export const useGetUserToken = (investmentAgreementId: string, options: UseQueryOptions = {}) => {
  const agreementApi = useApi(AgreementClient);

  return useQuery({
    queryKey: ["investment", "agreement", "token", investmentAgreementId],
    queryFn: () => agreementApi.getUserToken(investmentAgreementId),
    enabled: !!investmentAgreementId,
    ...options,
  });
};

export const useGetSigningPartyToken = (
  investmentAgreementId: string,
  signingPartyId: string,
  options: UseQueryOptions = {},
) => {
  const agreementApi = useApi(AgreementClient);

  return useQuery({
    queryKey: ["investment", "agreement", "token", investmentAgreementId, signingPartyId],
    queryFn: () => agreementApi.getSigningPartyToken(investmentAgreementId, signingPartyId),
    enabled: !!(investmentAgreementId && signingPartyId),
    ...options,
  });
};
